import React, { useState, useEffect, useRef } from 'react';
import { ClientEvent, clientEvents } from '../../data/clientEventsData';
import { fetchClientImages, GalleryImage } from '../../services/imageService';
import EventGalleryModal from '../shared/EventGalleryModal';

const TestimonialsIndustrial: React.FC = () => {
  const [selectedClient, setSelectedClient] = useState<ClientEvent | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Gallery modal state
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [loadingGalleryImages, setLoadingGalleryImages] = useState(false);

  // Handle event click to open gallery modal
  const handleEventClick = async (client: ClientEvent) => {
    setSelectedClient(client);
    setIsGalleryModalOpen(true);
    setLoadingGalleryImages(true);

    // Fetch images for the event
    try {
      const images = await fetchClientImages(client.id, client.company);
      setGalleryImages(images);
    } catch (error) {
      console.error('Error fetching images:', error);
      setGalleryImages([]);
    } finally {
      setLoadingGalleryImages(false);
    }
  };

  const handleCloseGalleryModal = () => {
    setIsGalleryModalOpen(false);
    setSelectedClient(null);
    setGalleryImages([]);
  };

  // Duplicar elementos para carrusel infinito
  const itemsToShow = 4; // Mostrar 4 elementos a la vez
  const duplicatedClients = [...clientEvents, ...clientEvents];

  // Auto-scroll del carrusel
  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setCurrentIndex(prevIndex => {
          const nextIndex = prevIndex + 1;
          if (nextIndex >= clientEvents.length) {
            return 0;
          }
          return nextIndex;
        });
      }, 3000); // Cambiar cada 3 segundos

      return () => clearInterval(interval);
    }
  }, [isHovered, clientEvents.length]);

  // Calcular transform para el carrusel
  const getTransform = () => {
    const itemWidth = 296; // Ancho de cada item (264px) + gap (32px)
    const offset = currentIndex * itemWidth;
    return `translateX(-${offset}px)`;
  };

  return (
    <section id="testimonios" className="section-industrial">
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div className="section-industrial-header">
          <h2 className="section-industrial-title">Empresas y eventos que han confiado en nuestro servicio</h2>
          <p className="section-industrial-subtitle">
            Respaldo de las principales organizaciones del país
          </p>
        </div>
        
        {/* Client Logos Carousel */}
        <div className="clients-carousel-container">
          <div 
            className="clients-carousel-wrapper"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div 
              ref={carouselRef}
              className="clients-carousel"
              style={{
                transform: getTransform(),
                transition: 'transform 0.5s ease-in-out'
              }}
            >
              {duplicatedClients.map((client, index) => (
                <div
                  key={`${client.id}-${index}`}
                  className="client-logo-card"
                  onClick={() => handleEventClick(client)}
                >
                  <div className="client-logo">
                    {client.image ? (
                      <img
                        src={client.image}
                        alt={client.company}
                        className="client-logo-image"
                      />
                    ) : (
                      <span className="logo-icon">{client.logo}</span>
                    )}
                  </div>
                  <div className="client-info">
                    <div className="client-name">{client.company}</div>
                    <div className="client-event-type">
                      {client.years.join(', ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Carousel Indicators */}
          <div className="carousel-indicators">
            {clientEvents.slice(0, 4).map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentIndex % 4 ? 'active' : ''}`}
                onClick={() => setCurrentIndex(index)}
              />
            ))}
          </div>
        </div>

        {/* Event Gallery Modal */}
        <EventGalleryModal
          isOpen={isGalleryModalOpen}
          selectedEvent={selectedClient}
          images={galleryImages}
          loadingImages={loadingGalleryImages}
          onClose={handleCloseGalleryModal}
        />
      </div>
    </section>
  );
};

// Add styles component
const styles = `
<style>
.clients-carousel-container {
  margin: calc(var(--industrial-unit) * 8) 0;
  position: relative;
}

.clients-carousel-wrapper {
  overflow: hidden;
  width: 100%;
  position: relative;
  padding: calc(var(--industrial-unit) * 2) 0;
}

.clients-carousel {
  display: flex;
  align-items: center;
  gap: calc(var(--industrial-unit) * 4);
  width: fit-content;
  animation: fadeInUp 1s ease;
}

.client-logo-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: calc(var(--industrial-unit) * 2);
  flex-shrink: 0;
  width: 264px;
}

/* Ensure grayscale is applied by default */
.client-logo-card .client-logo {
  filter: grayscale(100%) !important;
  -webkit-filter: grayscale(100%) !important;
  transition: filter 0.3s ease;
}

.client-logo-card:hover {
  transform: translateY(-8px) scale(1.05);
}

.client-logo-card:hover .client-logo {
  border-color: var(--industrial-accent);
  box-shadow: 0 12px 30px rgba(255, 107, 53, 0.3);
  transform: scale(1.05);
  filter: grayscale(0%) !important;
  -webkit-filter: grayscale(0%) !important;
}

.client-logo-card:hover .client-logo::before {
  opacity: 0.15;
}

.client-logo {
  background: var(--industrial-white);
  border: 3px solid var(--industrial-primary);
  border-radius: 50%;
  width: 228px;
  height: 228px;
  min-width: 228px;
  min-height: 228px;
  max-width: 228px;
  max-height: 228px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  aspect-ratio: 1;
}

.client-logo::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--industrial-primary), var(--industrial-accent));
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 50%;
}

.logo-icon {
  font-size: 6rem;
  display: block;
  position: relative;
  z-index: 2;
}

.client-logo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 50%;
  position: relative;
  z-index: 2;
}

.client-info {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: calc(var(--industrial-unit) * 0.5);
}

.client-name {
  font-family: var(--industrial-font-heading);
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--industrial-dark);
  text-transform: uppercase;
  letter-spacing: 1px;
  line-height: 1.2;
}

.client-event-type {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 0.7rem;
  color: var(--industrial-metallic);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: var(--industrial-font-secondary);
}


@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Carousel Indicators */
.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: calc(var(--industrial-unit) * 1);
  margin-top: calc(var(--industrial-unit) * 4);
}

.indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: rgba(74, 85, 104, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator.active {
  background: var(--industrial-primary);
  transform: scale(1.2);
}

.indicator:hover {
  background: var(--industrial-accent);
  transform: scale(1.1);
}

@media (max-width: 768px) {
  .clients-carousel {
    gap: calc(var(--industrial-unit) * 2);
  }
  
  .client-logo-card {
    width: 130px;
  }

  .client-logo {
    width: 110px;
    height: 110px;
    min-width: 110px;
    min-height: 110px;
    max-width: 110px;
    max-height: 110px;
  }

  .logo-icon {
    font-size: 2.8rem;
  }
  
  .client-name {
    font-size: 0.75rem;
  }
  
  .client-event-type {
    font-size: 0.65rem;
  }
  
  .carousel-indicators {
    margin-top: calc(var(--industrial-unit) * 3);
  }
  
  .indicator {
    width: 8px;
    height: 8px;
  }
}

@media (max-width: 480px) {
  .clients-carousel {
    gap: calc(var(--industrial-unit) * 1.5);
  }
  
  .client-logo-card {
    width: 110px;
  }

  .client-logo {
    width: 90px;
    height: 90px;
    min-width: 90px;
    min-height: 90px;
    max-width: 90px;
    max-height: 90px;
  }

  .logo-icon {
    font-size: 2.2rem;
  }
  
  .client-name {
    font-size: 0.7rem;
  }
  
  .client-event-type {
    font-size: 0.6rem;
  }
  
  .carousel-indicators {
    margin-top: calc(var(--industrial-unit) * 2);
  }
  
  .indicator {
    width: 6px;
    height: 6px;
  }
}
</style>
`;

// Inject styles
if (typeof document !== 'undefined') {
  let styleElement = document.getElementById('testimonials-styles');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'testimonials-styles';
    style.innerHTML = styles.replace('<style>', '').replace('</style>', '');
    document.head.appendChild(style);
  } else {
    // Update existing styles to ensure latest changes are applied
    styleElement.innerHTML = styles.replace('<style>', '').replace('</style>', '');
  }
}

export default TestimonialsIndustrial;